import React, { useCallback, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Plus, Copy, ArrowUp, ArrowDown, Trash2, ChevronDown, ChevronUp, Workflow as WorkflowIcon, Save, Undo2, Redo2, Pencil, X, Download, ArrowRightToLine, MoreHorizontal } from 'lucide-react';
import { EditableTitle } from './EditableTitle';
import { WYSIWYGEditor, EditorJSBlockData } from './WYSIWYGEditor';
import { useWorkflow } from '@/contexts/WorkflowContext';
import { editorJSToHtml } from '@/lib/editorjs-utils';
import { WorkflowStep } from '@/types/workflow';
import { BlockTypeToggles, type BlockType } from './BlockTypeSelector';
import { cn } from '@/lib/utils';
import AlertDialog from '@/components/ui/AlertDialog';
import { useAlertDialog } from '@/hooks/useAlertDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { parseInstructionToSubSteps, mergeSubStepsToText, isMultiInstruction } from '@/lib/instruction-utils';
import SendToSiteflowPopover from '@/components/SendToSiteflowPopover';

interface EditorPanelProps {
  className?: string;
  sendingToSiteflow?: boolean;
  siteflowError?: string;
  siteflowMessage?: string;
  onSendToSiteflow?: (projectId: string, familyId: string) => void;
}

export function EditorPanel({ className, sendingToSiteflow, siteflowError, siteflowMessage, onSendToSiteflow }: EditorPanelProps) {
  const {
    workflows,
    currentFlow,
    currentPhaseIndex,
    currentStepIndex,
    setCurrentFlow,
    updateFlow,
    isEditMode,
    hasUnsavedChanges,
    toggleEditMode,
    saveChanges,
    canUndo,
    canRedo,
    undo,
    redo,
    addToHistory,
    addPhase,
    addPhaseAfter,
    addStep,
    addStepAfter,
    duplicatePhase,
    movePhase,
    duplicateStep,
    moveStep,
    removePhase,
    removeStep,
    updateStepBlockType,
    toggleStepBlockType,
    convertStepBlockType,
    deactivateStepBlock,
    convertPhaseToStep,
    convertStepToPhase,
  } = useWorkflow();

  const [collapsedPhases, setCollapsedPhases] = useState<Set<number>>(new Set());
  const [collapsedSteps, setCollapsedSteps] = useState<Set<string>>(new Set());
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { alertState, showAlert, closeAlert } = useAlertDialog();
  
  // Store pending actions for confirmation dialogs
  const pendingActionRef = useRef<{
    type: 'deactivate' | 'convert' | 'update';
    phaseIndex: number;
    stepIndex: number;
    blockType?: BlockType;
    fromType?: BlockType;
    toType?: BlockType;
    blockToDeactivate?: string;
  } | null>(null);

  const currentFlowIndex = currentFlow ? workflows.findIndex(f => f === currentFlow) : -1;

  // Collapse everything by default (except first phase/step) to reduce initial render cost
  // Use a ref to track which flow has been initialized to avoid race conditions
  const initializedFlowRef = React.useRef<string | null>(null);
  const currentFlowId = currentFlow?.Flow?.name || null;
  
  React.useEffect(() => {
    // Only initialize when we have a new flow that hasn't been initialized
    if (!currentFlow || !currentFlowId) return;
    if (initializedFlowRef.current === currentFlowId) return;
    
    // Mark this flow as initialized BEFORE setting state to prevent re-runs
    initializedFlowRef.current = currentFlowId;
    
    // collapse all phases except first
    const phasesSet = new Set<number>();
    for (let i = 0; i < currentFlow.phases.length; i++) {
      if (i !== 0) phasesSet.add(i);
    }
    setCollapsedPhases(phasesSet);
    
    // collapse all steps except the first step of the first phase
    const stepsSet = new Set<string>();
    currentFlow.phases.forEach((phase, pi) => {
      phase.steps.forEach((_, si) => {
        if (!(pi === 0 && si === 0)) stepsSet.add(`${pi}-${si}`);
      });
    });
    setCollapsedSteps(stepsSet);
  }, [currentFlow, currentFlowId]);

  const handleConfirmAction = useCallback(() => {
    if (!pendingActionRef.current) return;
    const action = pendingActionRef.current;
    
    if (action.type === 'deactivate' && action.blockToDeactivate) {
      deactivateStepBlock(currentFlowIndex, action.phaseIndex, action.stepIndex, action.blockToDeactivate);
    } else if (action.type === 'convert' && action.fromType && action.toType) {
      convertStepBlockType(currentFlowIndex, action.phaseIndex, action.stepIndex, action.fromType, action.toType);
    } else if (action.type === 'update' && action.blockType) {
      updateStepBlockType(currentFlowIndex, action.phaseIndex, action.stepIndex, action.blockType);
    }
    
    pendingActionRef.current = null;
  }, [currentFlowIndex, deactivateStepBlock, convertStepBlockType, updateStepBlockType]);

  const handleToggleBlockType = useCallback((phaseIndex: number, stepIndex: number, blockType: BlockType) => {
    const result = toggleStepBlockType(currentFlowIndex, phaseIndex, stepIndex, blockType);
    if (result.needsDeactivationConfirmation) {
      pendingActionRef.current = {
        type: 'deactivate',
        phaseIndex,
        stepIndex,
        blockToDeactivate: result.blockToDeactivate,
      };
      showAlert({
        title: 'Supprimer le bloc',
        message: 'Ce bloc contient du contenu. Êtes-vous sûr de vouloir le supprimer ?',
        confirmText: 'Supprimer',
        cancelText: 'Annuler',
        variant: 'danger',
        confirmButtonVariant: 'danger',
        onConfirm: handleConfirmAction,
      });
    } else if (result.needsConversion) {
      pendingActionRef.current = {
        type: 'convert',
        phaseIndex,
        stepIndex,
        blockType,
        fromType: result.fromType,
        toType: result.toType,
      };
      showAlert({
        title: 'Convertir le contenu',
        message: `Voulez-vous convertir le contenu existant de ${result.fromType} vers ${result.toType} ?`,
        confirmText: 'Convertir',
        cancelText: 'Remplacer',
        variant: 'info',
        confirmButtonVariant: 'primary',
        onConfirm: handleConfirmAction,
      });
    }
  }, [currentFlowIndex, toggleStepBlockType, showAlert, handleConfirmAction]);

  const getActiveBlockTypes = (step: WorkflowStep): BlockType[] => {
    const active: BlockType[] = [];
    if (step.blocks.instruction) active.push('instruction');
    return active;
  };

  const togglePhaseCollapse = (phaseIndex: number) => {
    const newSet = new Set(collapsedPhases);
    const isCurrentlyCollapsed = newSet.has(phaseIndex);
    
    if (isCurrentlyCollapsed) {
      // Phase is being expanded - remove from collapsed phases
      newSet.delete(phaseIndex);
      
      // Also expand all steps within this phase
      if (currentFlow && currentFlow.phases[phaseIndex]) {
        const newStepsSet = new Set(collapsedSteps);
        currentFlow.phases[phaseIndex].steps.forEach((_, stepIndex) => {
          const stepKey = `${phaseIndex}-${stepIndex}`;
          newStepsSet.delete(stepKey);
        });
        setCollapsedSteps(newStepsSet);
      }
    } else {
      // Phase is being collapsed
      newSet.add(phaseIndex);
    }
    
    setCollapsedPhases(newSet);
  };

  const toggleStepCollapse = (phaseIndex: number, stepIndex: number) => {
    const key = `${phaseIndex}-${stepIndex}`;
    const newSet = new Set(collapsedSteps);
    if (newSet.has(key)) newSet.delete(key); else newSet.add(key);
    setCollapsedSteps(newSet);
  };

  const handleAddPhase = () => { if (currentFlowIndex < 0) return; addPhase(currentFlowIndex); addToHistory('Phase ajoutée'); };
  const handleAddPhaseAfter = (phaseIndex: number) => { if (currentFlowIndex < 0) return; addPhaseAfter(currentFlowIndex, phaseIndex); addToHistory('Phase ajoutée'); };
  const handleRemovePhase = (phaseIndex: number) => {
    if (currentFlowIndex < 0) return;
    showAlert({
      title: 'Supprimer la phase',
      message: 'Êtes-vous sûr de vouloir supprimer cette phase ?',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      variant: 'danger',
      confirmButtonVariant: 'danger',
      onConfirm: () => { removePhase(currentFlowIndex, phaseIndex); addToHistory('Phase supprimée'); },
    });
  };
  const handleDuplicatePhase = (phaseIndex: number) => { if (currentFlowIndex < 0) return; duplicatePhase(currentFlowIndex, phaseIndex); addToHistory('Phase dupliquée'); };
  const handleMovePhase = (phaseIndex: number, direction: 'up' | 'down') => { if (currentFlowIndex < 0) return; movePhase(currentFlowIndex, phaseIndex, direction); addToHistory('Phase déplacée'); };
  const handleConvertPhaseToStep = (phaseIndex: number) => { if (currentFlowIndex < 0) return; convertPhaseToStep(currentFlowIndex, phaseIndex); addToHistory('Phase convertie en étape'); };

  const handleAddStep = (phaseIndex: number) => { if (currentFlowIndex < 0) return; addStep(currentFlowIndex, phaseIndex); addToHistory('Étape ajoutée'); };
  const handleAddStepAfter = (phaseIndex: number, stepIndex: number) => { if (currentFlowIndex < 0) return; addStepAfter(currentFlowIndex, phaseIndex, stepIndex); addToHistory('Étape ajoutée'); };
  const handleRemoveStep = (phaseIndex: number, stepIndex: number) => {
    if (currentFlowIndex < 0) return;
    showAlert({
      title: 'Supprimer l\'étape',
      message: 'Êtes-vous sûr de vouloir supprimer cette étape ?',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      variant: 'danger',
      confirmButtonVariant: 'danger',
      onConfirm: () => { removeStep(currentFlowIndex, phaseIndex, stepIndex); addToHistory('Étape supprimée'); },
    });
  };
  const handleDuplicateStep = (phaseIndex: number, stepIndex: number) => { if (currentFlowIndex < 0) return; duplicateStep(currentFlowIndex, phaseIndex, stepIndex); addToHistory('Étape dupliquée'); };
  const handleMoveStep = (phaseIndex: number, stepIndex: number, direction: 'up' | 'down') => { if (currentFlowIndex < 0) return; moveStep(currentFlowIndex, phaseIndex, stepIndex, direction); addToHistory('Étape déplacée'); };
  const handleConvertStepToPhase = (phaseIndex: number, stepIndex: number) => { if (currentFlowIndex < 0) return; convertStepToPhase(currentFlowIndex, phaseIndex, stepIndex); addToHistory('Étape convertie en phase'); };

  const handleCreateStepFromSelection = useCallback((phaseIndex: number, stepIndex: number, selectedText: string) => {
    if (currentFlowIndex < 0 || !selectedText.trim()) return;
    const newStep: WorkflowStep = {
      name: 'Nouvelle étape',
      blocks: { instruction: { text: selectedText.trim() } },
    };
    addStepAfter(currentFlowIndex, phaseIndex, stepIndex, newStep);
    addToHistory('Étape créée depuis sélection');
  }, [currentFlowIndex, addStepAfter, addToHistory]);

  const handleMoveBlockOut = useCallback((phaseIndex: number, stepIndex: number, direction: 'up' | 'down', block: EditorJSBlockData, remainingHtml: string) => {
    if (!currentFlow) return;
    const blockHtml = editorJSToHtml({ time: Date.now(), blocks: [block], version: '2.30.8' });
    if (!blockHtml) return;

    let targetPhaseIndex = phaseIndex;
    let targetStepIndex = direction === 'up' ? stepIndex - 1 : stepIndex + 1;

    if (direction === 'up' && targetStepIndex < 0) {
      targetPhaseIndex = phaseIndex - 1;
      if (targetPhaseIndex < 0) return;
      targetStepIndex = currentFlow.phases[targetPhaseIndex].steps.length - 1;
    } else if (direction === 'down' && targetStepIndex >= currentFlow.phases[phaseIndex].steps.length) {
      targetPhaseIndex = phaseIndex + 1;
      if (targetPhaseIndex >= currentFlow.phases.length) return;
      targetStepIndex = 0;
    }

    const targetStep = currentFlow.phases[targetPhaseIndex]?.steps[targetStepIndex];
    if (!targetStep?.blocks?.instruction) return;

    const updatedFlow = JSON.parse(JSON.stringify(currentFlow));
    // Update current step with remaining content
    updatedFlow.phases[phaseIndex].steps[stepIndex].blocks.instruction.text = remainingHtml;
    // Add block to target step
    const targetText = updatedFlow.phases[targetPhaseIndex].steps[targetStepIndex].blocks.instruction.text || '';
    if (direction === 'up') {
      updatedFlow.phases[targetPhaseIndex].steps[targetStepIndex].blocks.instruction.text = targetText + (targetText ? '\n' : '') + blockHtml;
    } else {
      updatedFlow.phases[targetPhaseIndex].steps[targetStepIndex].blocks.instruction.text = blockHtml + (targetText ? '\n' : '') + targetText;
    }
    updateFlow(updatedFlow);
    addToHistory('Bloc déplacé');
  }, [currentFlow, updateFlow, addToHistory]);

  const handleConvertMultiInstruction = useCallback((phaseIndex: number, stepIndex: number) => {
    if (!currentFlow) return;
    const step = currentFlow.phases[phaseIndex]?.steps[stepIndex];
    if (!step?.blocks?.instruction) return;

    const instruction = step.blocks.instruction;
    
    if (isMultiInstruction(instruction)) {
      // Convert from multi to simple: merge everything back
      const mergedText = mergeSubStepsToText(instruction.text, instruction.subInstructions || []);
      const updatedFlow = { ...currentFlow };
      updatedFlow.phases[phaseIndex].steps[stepIndex].blocks.instruction = {
        text: mergedText
      };
      updateFlow(updatedFlow);
      addToHistory('Instruction convertie en simple');
    } else {
      // Convert from simple to multi: parse headers
      const { introText, subInstructions, hasHeaders } = parseInstructionToSubSteps(instruction.text);
      
      if (!hasHeaders) {
        showAlert({
          title: 'Conversion impossible',
          message: 'Aucun titre markdown (## ou ###) trouvé dans le contenu. Ajoutez des titres pour créer des sous-instructions.',
          confirmText: 'OK',
          cancelText: '',
          variant: 'info',
          confirmButtonVariant: 'primary',
          onConfirm: () => {},
        });
        return;
      }
      
      const updatedFlow = { ...currentFlow };
      updatedFlow.phases[phaseIndex].steps[stepIndex].blocks.instruction = {
        text: introText,
        subInstructions
      };
      updateFlow(updatedFlow);
      addToHistory('Instruction convertie en multi-instruction');
    }
  }, [currentFlow, updateFlow, addToHistory, showAlert]);

  if (!currentFlow) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Aucun workflow sélectionné</p>
      </div>
    );
  }

  return (
    <div className={cn('bg-editor flex flex-col h-full', className)}>
      <header className="bg-card border-b border-border px-6 py-4">
        <div className="flex flex-col">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <WorkflowIcon className="h-4 w-4" />
            <span>{currentFlow.Flow.name}</span>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button variant={isEditMode ? 'ghost' : 'outline'} size="sm" onClick={toggleEditMode} className="flex items-center space-x-2">
              {isEditMode ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
              <span>{isEditMode ? 'Fermer' : 'Editer'}</span>
            </Button>
            {isEditMode && (
              <>
                <div className="flex items-center space-x-1">
                  <Button variant="ghost" size="sm" onClick={undo} disabled={!canUndo()} className="p-2"><Undo2 className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={redo} disabled={!canRedo()} className="p-2"><Redo2 className="h-4 w-4" /></Button>
                </div>
                <Button variant={hasUnsavedChanges ? 'default' : 'ghost'} size="sm" onClick={saveChanges} disabled={!hasUnsavedChanges} className="flex items-center space-x-2">
                  <Save className="h-4 w-4" />
                  <span>{hasUnsavedChanges ? 'Sauvegarder' : 'Sauvegardé'}</span>
                </Button>
              </>
            )}
            <SendToSiteflowPopover
              onConfirm={async (projectId, familyId) => onSendToSiteflow?.(projectId, familyId)}
              sending={!!sendingToSiteflow}
              sendError={siteflowError}
              sendSuccess={siteflowMessage}
              disabled={!onSendToSiteflow}
            >
              <Button
                variant="ghost"
                size="sm"
                disabled={!onSendToSiteflow || sendingToSiteflow}
                className="flex items-center space-x-2 ml-auto"
              >
                {sendingToSiteflow ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    <span>Send to Siteflow</span>
                  </>
                )}
              </Button>
            </SendToSiteflowPopover>
          </div>
        </div>
      </header>

      <div className="flex-1 h-0 overflow-hidden">
        <ScrollArea ref={scrollAreaRef} className="h-full">
          <div className="p-6 space-y-4">
            {currentFlow.phases.map((phase, phaseIndex) => (
              <Collapsible key={phaseIndex} open={!collapsedPhases.has(phaseIndex)} onOpenChange={() => togglePhaseCollapse(phaseIndex)} className="border border-border rounded-lg bg-card shadow-sm">
                <CollapsibleTrigger asChild>
                  <div className="flex items-center justify-between p-4 hover:bg-accent/50 cursor-pointer">
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center space-x-2">
                        {collapsedPhases.has(phaseIndex) ? (<ChevronDown className="h-4 w-4" />) : (<ChevronUp className="h-4 w-4" />)}
                        <span className="font-medium text-sm text-muted-foreground">Phase {phaseIndex + 1}</span>
                      </div>
                      <EditableTitle value={phase.name} onChange={(newName) => { const updatedFlow = { ...currentFlow }; updatedFlow.phases[phaseIndex].name = newName; updateFlow(updatedFlow); addToHistory('Phase renommée'); }} isEditMode={isEditMode} className="text-lg font-semibold" />
                    </div>
                    {isEditMode && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => handleAddPhaseAfter(phaseIndex)}>
                            <Plus className="mr-2 h-4 w-4" />
                            Ajouter une phase
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicatePhase(phaseIndex)}>
                            <Copy className="mr-2 h-4 w-4" />
                            Dupliquer
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleMovePhase(phaseIndex, 'up')} disabled={phaseIndex === 0}>
                            <ArrowUp className="mr-2 h-4 w-4" />
                            Déplacer vers le haut
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleMovePhase(phaseIndex, 'down')} disabled={phaseIndex === currentFlow.phases.length - 1}>
                            <ArrowDown className="mr-2 h-4 w-4" />
                            Déplacer vers le bas
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleConvertPhaseToStep(phaseIndex)}>
                            <ArrowRightToLine className="mr-2 h-4 w-4" />
                            Convertir en étape
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleRemovePhase(phaseIndex)} className="text-destructive focus:text-destructive">
                            <Trash2 className="mr-2 h-4 w-4" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent className="border-t border-border">
                  <div className="p-4 space-y-3">
                    {phase.steps.map((step, stepIndex) => {
                      const stepKey = `${phaseIndex}-${stepIndex}`;
                      const isStepCollapsed = collapsedSteps.has(stepKey);
                      return (
                        <Collapsible key={stepIndex} open={!isStepCollapsed} onOpenChange={() => toggleStepCollapse(phaseIndex, stepIndex)} className="border border-border rounded-md bg-background">
                          <CollapsibleTrigger asChild>
                            <div className="flex items-center justify-between p-3 hover:bg-accent/30 cursor-pointer">
                              <div className="flex items-center space-x-2">
                                <div className="flex items-center space-x-2">{isStepCollapsed ? (<ChevronDown className="h-4 w-4" />) : (<ChevronUp className="h-4 w-4" />)}<span className="font-medium text-xs text-muted-foreground">Étape {stepIndex + 1}</span></div>
                                <EditableTitle value={step.name} onChange={(newName) => { const updatedFlow = { ...currentFlow }; updatedFlow.phases[phaseIndex].steps[stepIndex].name = newName; updateFlow(updatedFlow); addToHistory('Étape renommée'); }} isEditMode={isEditMode} className="font-medium" />
                              </div>
                              {isEditMode && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                                      <MoreHorizontal className="h-3 w-3" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-48">
                                    <DropdownMenuItem onClick={() => handleAddStepAfter(phaseIndex, stepIndex)}>
                                      <Plus className="mr-2 h-4 w-4" />
                                      Ajouter une étape
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleDuplicateStep(phaseIndex, stepIndex)}>
                                      <Copy className="mr-2 h-4 w-4" />
                                      Dupliquer
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleMoveStep(phaseIndex, stepIndex, 'up')} disabled={stepIndex === 0 && phaseIndex === 0}>
                                      <ArrowUp className="mr-2 h-4 w-4" />
                                      Déplacer vers le haut
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleMoveStep(phaseIndex, stepIndex, 'down')} disabled={stepIndex === phase.steps.length - 1 && phaseIndex === currentFlow.phases.length - 1}>
                                      <ArrowDown className="mr-2 h-4 w-4" />
                                      Déplacer vers le bas
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleConvertStepToPhase(phaseIndex, stepIndex)}>
                                      <ArrowRightToLine className="mr-2 h-4 w-4" />
                                      Convertir en phase
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => handleRemoveStep(phaseIndex, stepIndex)} className="text-destructive focus:text-destructive">
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Supprimer
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent className="border-t border-border">
                            <div className="p-4 space-y-4">
                              {isEditMode && (
                                <div className="mb-4">
                                  <BlockTypeToggles 
                                    activeBlocks={getActiveBlockTypes(step)} 
                                    onToggleBlock={(blockType) => handleToggleBlockType(phaseIndex, stepIndex, blockType)}
                                    isMultiInstruction={isMultiInstruction(step.blocks.instruction)}
                                    onConvertMultiInstruction={() => handleConvertMultiInstruction(phaseIndex, stepIndex)}
                                  />
                                </div>
                              )}
                              {step.blocks.instruction && (
                                <div className="space-y-2">
                                  {/* Show intro text if present or if not in multi-instruction mode */}
                                  {(step.blocks.instruction.text || !isMultiInstruction(step.blocks.instruction)) && (
                                    <>
                                      <h4 className="font-medium text-sm">Instruction</h4>
                                      <WYSIWYGEditor 
                                        key={`instruction-${phaseIndex}-${stepIndex}-${isEditMode}`} 
                                        value={step.blocks.instruction.text} 
                                        onChange={(content) => { 
                                          const updatedFlow = { ...currentFlow }; 
                                          updatedFlow.phases[phaseIndex].steps[stepIndex].blocks.instruction!.text = content; 
                                          updateFlow(updatedFlow); 
                                        }} 
                                        readOnly={!isEditMode} 
                                        placeholder="Saisissez l'instruction..." 
                                        phaseIndex={phaseIndex} 
                                        stepIndex={stepIndex}
                                        onMoveBlockOut={(dir, blk, html) => handleMoveBlockOut(phaseIndex, stepIndex, dir, blk, html)}
                                        onCreateStepFromSelection={(text) => handleCreateStepFromSelection(phaseIndex, stepIndex, text)}
                                      />
                                    </>
                                  )}
                                  
                                  {/* Show sub-instructions if in multi-instruction mode */}
                                  {isMultiInstruction(step.blocks.instruction) && step.blocks.instruction.subInstructions?.map((subInstruction, subIndex) => (
                                    <div key={`sub-${phaseIndex}-${stepIndex}-${subIndex}`} className="border-l-2 border-primary/20 pl-2 space-y-2 mb-4 pb-2">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-muted-foreground font-medium">Sous-instruction {subIndex + 1}</span>
                                      </div>
                                      {isEditMode ? (
                                        <input
                                          type="text"
                                          value={subInstruction.title}
                                          onChange={(e) => {
                                            const updatedFlow = { ...currentFlow };
                                            updatedFlow.phases[phaseIndex].steps[stepIndex].blocks.instruction!.subInstructions![subIndex].title = e.target.value;
                                            updateFlow(updatedFlow);
                                          }}
                                          className="w-full text-sm font-semibold p-1 border border-border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                                          placeholder="Titre de la sous-instruction"
                                        />
                                      ) : (
                                        <h5 className="text-sm font-semibold">{subInstruction.title}</h5>
                                      )}
                                      <WYSIWYGEditor
                                        key={`sub-instruction-${phaseIndex}-${stepIndex}-${subIndex}-${isEditMode}`}
                                        value={subInstruction.content}
                                        onChange={(content) => {
                                          const updatedFlow = { ...currentFlow };
                                          updatedFlow.phases[phaseIndex].steps[stepIndex].blocks.instruction!.subInstructions![subIndex].content = content;
                                          updateFlow(updatedFlow);
                                        }}
                                        readOnly={!isEditMode}
                                        placeholder="Contenu de la sous-instruction..."
                                        phaseIndex={phaseIndex}
                                        stepIndex={stepIndex}
                                        onCreateStepFromSelection={(text) => handleCreateStepFromSelection(phaseIndex, stepIndex, text)}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                    {isEditMode && (
                      <Button variant="outline" onClick={() => handleAddStep(phaseIndex)} className="w-full border-dashed border-2 hover:bg-accent/50">
                        <Plus className="h-4 w-4 mr-2" />
                        Ajouter une étape
                      </Button>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))}
            {isEditMode && (
              <Button variant="outline" onClick={handleAddPhase} className="w-full border-dashed border-2 hover:bg-accent/50">
                <Plus className="h-4 w-4 mr-2" />
                Ajouter une phase
              </Button>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog
        isOpen={alertState.isOpen}
        onClose={closeAlert}
        onConfirm={alertState.onConfirm}
        title={alertState.title}
        message={alertState.message}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        variant={alertState.variant}
        confirmButtonVariant={alertState.confirmButtonVariant}
      />
    </div>
  );
}


